/**
 * ImportSourcePanel - Session Capture left rail (Sprint 3.4 REDO).
 *
 * Renders the "Import source" card. The prototype shows a blue dot
 * status badge ("Connected") next to a "Scrips PROM Engine" summary
 * with: 119 validated clinical scoring instruments, FHIR
 * QuestionnaireResponse output, and 6 Cardiometabolic-Pathway-seeded instruments.
 *
 * Pure presentation - no mutating state. The connection status is
 * static for the demo; Sprint 4.x can wire this to a real health
 * probe.
 *
 * HC2 token-only.
 * HC5 zero em / en dashes.
 */
export interface ImportSourcePanelProps {
  /** Total instrument count surfaced in the source card (default 119). */
  totalInstruments?: number;
  /** Cardiometabolic-Pathway-seeded instrument count (default 6). */
  seededInstruments?: number;
  /** Connection status string. Defaults to "Connected". */
  status?: 'Connected' | 'Disconnected';
}

export function ImportSourcePanel({
  totalInstruments = 119,
  seededInstruments = 6,
  status = 'Connected',
}: ImportSourcePanelProps) {
  return (
    <aside
      data-testid="import-source-panel"
      aria-labelledby="import-source-title"
      className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
    >
      <header className="flex items-center gap-gutter-sm">
        <span
          aria-hidden="true"
          data-testid="import-source-status-dot"
          className="inline-block h-2.5 w-2.5 rounded-signal-pill bg-signal-primary-500"
        />
        <p className="text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary">
          Import source
        </p>
      </header>
      <h3
        id="import-source-title"
        className="mt-gutter-sm text-signal-lg font-semibold text-text-primary"
      >
        Scrips PROM Engine
      </h3>
      <span
        role="status"
        data-testid="import-source-status-badge"
        className="mt-gutter-sm inline-flex items-center gap-gutter-sm rounded-signal-pill bg-status-active-bg px-gutter-md py-0.5 text-signal-xs font-semibold text-status-active-fg"
      >
        {status}
      </span>
      <dl className="mt-gutter-md space-y-gutter-sm text-signal-sm text-text-secondary">
        <div className="flex justify-between gap-gutter-md">
          <dt className="text-text-tertiary">Validated instruments</dt>
          <dd
            data-testid="import-source-total"
            className="font-semibold text-text-primary"
          >
            {totalInstruments}
          </dd>
        </div>
        <div className="flex justify-between gap-gutter-md">
          <dt className="text-text-tertiary">Output</dt>
          <dd className="text-text-primary">FHIR QuestionnaireResponse</dd>
        </div>
        <div className="flex justify-between gap-gutter-md">
          <dt className="text-text-tertiary">Pathway-seeded</dt>
          <dd
            data-testid="import-source-seeded"
            className="font-semibold text-text-primary"
          >
            {seededInstruments}
          </dd>
        </div>
      </dl>
      <p className="mt-gutter-md text-signal-xs text-text-tertiary">
        Selection feeds the FHIR PlanDefinition publish path.
      </p>
    </aside>
  );
}
